import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import './compiled-data.styles.scss'
import { useSelector } from 'react-redux';
import { ModalAlert } from '../../SHARED/modal/modal.component';
import { getQueryFromFirestore } from '../../../utils/getDataFromDbSimplified';
import { US_STATES } from '../../../utils/states';
import { Sources } from '../../../forms/formTypes';
import EvalResultsDisplay from '../manage-evals/eval-results-display.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

const CompiledData = ({ evalList }) => {

  const screenSize = useSelector(state => state.listsData.screenSize);
  const [questionSets, setQuestionSets] = useState(null);
  const [selectedQuestionSet, setSelectedQuestionSet] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedCpdu, setSelectedCpdu] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userEvaluations, setUserEvaluations] = useState(null);
  const [displayQuestionSets, setDisplayQuestionSets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chunkProgress, setChunkProgress] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  useEffect(() => {
    getQuestionSets();
  }, [])

  function getQuestionSets() {
    getQueryFromFirestore({
      path: 'forms_data',
      queryArray: [{ field: 'type', operator: '==', value: 'questionSet' }],
      once: true,
      callback: (data) => {
        const sorted = Object.values(data).sort((a, b) =>
          (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '')
        );
        setQuestionSets(sorted);
      }
    });
  }

  function handleGetData() {
    if (!selectedQuestionSet) {
      setAlertMessage('Please select a Question Set.');
      return;
    }
    if (!startDate) {
      setAlertMessage('Please select a Start Date.');
      return;
    }

    setLoading(true);
    setChunkProgress(null);
    setUserEvaluations(null);
    setDisplayQuestionSets(null);

    const baseFilters = [];
    if (selectedState) {
      baseFilters.push({ field: 'state', operator: '==', value: selectedState });
    }
    if (selectedSource) {
      baseFilters.push({ field: 'evalSource', operator: '==', value: selectedSource });
    }
    if (selectedCpdu === 'yes') {
      baseFilters.push({ field: 'cpdu', operator: '==', value: true });
    } else if (selectedCpdu === 'no') {
      baseFilters.push({ field: 'cpdu', operator: '==', value: false });
    }
    if (startDate) {
      const [sy, sm, sd] = startDate.split('-').map(Number);
      baseFilters.push({ field: 'ts', operator: '>=', value: new Date(sy, sm - 1, sd, 0, 0, 0, 0).getTime() });
    }
    if (endDate) {
      const [ey, em, ed] = endDate.split('-').map(Number);
      baseFilters.push({ field: 'ts', operator: '<=', value: new Date(ey, em - 1, ed, 23, 59, 59, 999).getTime() });
    }

    if (evalList) {
      const matchingEvals = Object.values(evalList).filter(ev =>
        ev.questionSets && ev.questionSets.includes(selectedQuestionSet)
      );

      if (matchingEvals.length === 0) {
        setAlertMessage('No evaluations found using the selected question set.');
        setLoading(false);
        return;
      }

      // const allQSetIds = [...new Set(matchingEvals.flatMap(ev => ev.questionSets || []))];
      // const setsToDisplay = allQSetIds
      //   .map(id => questionSets.find(qs => qs.id === id))
      //   .filter(Boolean);
      setDisplayQuestionSets(questionSets.find(qs => qs.id === selectedQuestionSet) ? [questionSets.find(qs => qs.id === selectedQuestionSet)] : null);

      const allEvalIds = matchingEvals.map(ev => ev.id);
      const chunks = [];
      for (let i = 0; i < allEvalIds.length; i += 30) {
        chunks.push(allEvalIds.slice(i, i + 30));
      }

      let mergedResults = {};
      let completedChunks = 0;
      setChunkProgress({ completed: 0, total: chunks.length });

      for (const chunk of chunks) {
        const idFilter = chunk.length === 1
          ? { field: 'evalId', operator: '==', value: chunk[0] }
          : { field: 'evalId', operator: 'in', value: chunk };

        getQueryFromFirestore({
          path: 'userEvaluations',
          queryArray: [idFilter, ...baseFilters],
          once: true,
          callback: (data) => {
            mergedResults = { ...mergedResults, ...data };
            completedChunks++;
            if (completedChunks === chunks.length) {
              setUserEvaluations(mergedResults);
              setChunkProgress(null);
              setLoading(false);
            } else {
              flushSync(() => {
                setChunkProgress({ completed: completedChunks, total: chunks.length });
              });
            }
          }
        });
      }
    } else {
      const selectedSet = questionSets.find(qs => qs.id === selectedQuestionSet);
      setDisplayQuestionSets(selectedSet ? [selectedSet] : null);

      getQueryFromFirestore({
        path: 'userEvaluations',
        queryArray: baseFilters.length > 0 ? baseFilters : undefined,
        once: true,
        callback: (data) => {
          setUserEvaluations(data);
          setLoading(false);
        }
      });
    }
  }

  return (
    <div className='compiled-data'>
      <div className='compiled-data-card g-card'>
      <h3>Get Compiled Data</h3>
      <hr />
      <div className={`filter-row ${screenSize}`}>
        <div className='filter-field'>
          <label>Question Set <span className='required'>*</span></label>
          <select
            className='uk-select'
            value={selectedQuestionSet}
            onChange={e => setSelectedQuestionSet(e.target.value)}>
            <option value=''>-- Select Question Set --</option>
            {questionSets && questionSets.map(qs => (
              <option key={qs.id} value={qs.id}>{qs.displayName || qs.name}</option>
            ))}
          </select>
        </div>
        <div className='filter-field'>
          <label>State</label>
          <select
            className='uk-select'
            value={selectedState}
            onChange={e => setSelectedState(e.target.value)}>
            <option value=''>-- All States --</option>
            {US_STATES
            .filter(s => s.active)
            .map(s => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className='filter-field'>
          <label>Source</label>
          <select
            className='uk-select'
            value={selectedSource}
            onChange={e => setSelectedSource(e.target.value)}>
            <option value=''>-- All Sources --</option>
            {Object.values(Sources).map(s => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>
        </div>
        <div className='filter-field'>
          <label>CPDUs</label>
          <select
            className='uk-select'
            value={selectedCpdu}
            onChange={e => setSelectedCpdu(e.target.value)}>
            <option value=''>-- All --</option>
            <option value='yes'>Yes</option>
            <option value='no'>No</option>
          </select>
        </div>
        <div className='filter-field'>
          <label>Start Date</label>
          <input
            type='date'
            className='uk-input'
            value={startDate}
            onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className='filter-field'>
          <label>End Date</label>
          <input
            type='date'
            className='uk-input'
            value={endDate}
            onChange={e => setEndDate(e.target.value)} />
        </div>
        <div className='filter-field button-field'>
          <button
            className='g-button med-btn'
            onClick={handleGetData}
            disabled={loading || !questionSets}>
            {loading
              ? chunkProgress
                ? `Loading... ${Math.round((chunkProgress.completed / chunkProgress.total) * 100)}%`
                : 'Loading...'
              : 'Get Data'}
          </button>
        </div>
      </div>
      <div className='g-space-1'></div>
      {
        (displayQuestionSets && userEvaluations) &&
        <EvalResultsDisplay
          questionSets={displayQuestionSets}
          userEvaluations={userEvaluations}
          evaluationDataList={evalList} />
      }
      </div>
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
    </div>
  )
}

export default CompiledData
